import React, { useEffect, useState } from 'react';
import styles from 'styles/components/Similarity.module.sass';
import ReusableIcon from './ReusableIcon';

/**
 * Props for the Similarity component.
 * 
 * @interface SimilarityProps
 * @property {number} percentage - The similarity percentage to display.
 */
interface SimilarityProps {
    percentage: number;
}

/**
 * Configuration for similarity levels.
 * Each level has a label, icon, and color associated with it.
 */
interface SimilarityConfig {
    label: string;
    level: string;
    icon: string;
    color: string;
}

/**
 * Returns a configuration object based on the similarity percentage.
 * 
 * @param {number} percentage - The similarity percentage.
 * @returns {SimilarityConfig} The configuration object containing label, level, icon, and color.
 */
const getSimilarityConfig = (percentage: number): SimilarityConfig => {
    switch (true) {
        case percentage >= 90:
            return {
                label: 'Excellent Match',
                level: 'excellent',
                icon: 'mynaui:check-circle',
                color: '#22C55E'
            };
        case percentage >= 80:
            return {
                label: 'Good Match',
                level: 'good',
                icon: 'mynaui:trending-up',
                color: '#3B82F6'
            };
        case percentage >= 70:
            return {
                label: 'Fair Match',
                level: 'average',
                icon: 'mynaui:info-circle',
                color: '#F59E0B'
            };
        default:
            return {
                label: 'Low Match',
                level: 'poor',
                icon: 'mynaui:info-circle',
                color: '#EF4444'
            };
    }
}

/**
 * Similarity component displays the match confidence level based on a percentage.
 * It includes a progress bar and a badge indicating the match level.
 * 
 * @component
 * @param {SimilarityProps} props - The properties for the Similarity component.
 * @param {number} props.percentage - The similarity percentage to display.
 * 
 * @example
 * <Similarity percentage={85} />
 * This will render a badge indicating a "Good Match" with a progress bar showing 85%.
 *
 * @returns {JSX.Element} The rendered Similarity component.
 */
const Similarity: React.FC<SimilarityProps> = ({ percentage }) => {
    const { label, level, icon, color } = getSimilarityConfig(percentage);
    //const [roundedPercentage, setRoundedPercentage] = useState(Math.round(percentage));
    const [progressWidth, setProgressWidth] = useState(percentage);

    // Update the progress width based on the percentage
    useEffect(() => {
        //setRoundedPercentage(Math.round(percentage));
        setProgressWidth(percentage);
    }, [percentage]);

    return (
        <div className={styles['similarity-container']}>
            <div className={styles['confidence-header']}>
                <span className={styles['label']}>Match Confidence</span>
                <div className={`${styles['badge']} ${styles[`badge--${level}`]}`}>
                    <ReusableIcon icon={icon} color={color} className={styles['icon'] || 'icon'} />
                    <span className={styles['badge-text']}>{label}</span>
                </div>
            </div>
            <div className={styles['progress-section']}>
                <div className={styles['progress-bar']}>
                    <div 
                        className={`${styles['progress-fill']} ${styles[`progress-fill--${level}`]}`} 
                        style={{ width: `${progressWidth}%` }}>
                    </div>
                </div>
                {/* <div className={styles['progress-indicators']}>
                    <span className={styles['min-value']}>0%</span>
                    <div className={styles['score-display']}>
                        <span className={`${styles['score']} ${styles[`score--${level}`]}`}>
                            {roundedPercentage}%
                        </span>
                    </div>
                        
                    <span className={styles['max-value']}>100%</span>
                </div> */}
            </div>
        </div>
    );
};

export default Similarity;